import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import { getStatus, getErrorDetail } from '@shared/api/httpErrors'
import { getProfile, useInvalidateProfile } from '@entities/user'
import { useAppStore } from '@/store/useAppStore'
import { usePlatform } from '@shared/platform'
import { ROUTES } from '@shared/config/routes'
import { LinkEmailModal } from '@features/link-email/ui/LinkEmailModal'
import {
  checkTelegramAuthToken,
  confirmTelegramAuthToken
} from '../api/telegramAuthApi'
import styles from './TelegramAuthConfirmScreen.module.css'

type Status = 'checking' | 'preview' | 'confirming' | 'success' | 'error'

/**
 * Экран в TG mini-app для подтверждения web-авторизации через Telegram.
 * Открывается, когда start_param начинается с `telegram_auth_`.
 * Если у профиля нет email — открывает LinkEmailModal и после привязки повторяет confirm.
 */
export const TelegramAuthConfirmScreen = () => {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()
  const invalidateProfile = useInvalidateProfile()
  const setProfile = useAppStore((s) => s.setProfile)
  const platform = usePlatform()

  const [status, setStatus] = useState<Status>('checking')
  const [errorText, setErrorText] = useState('')
  const [linkEmailOpen, setLinkEmailOpen] = useState(false)
  const [pendingEmailRetry, setPendingEmailRetry] = useState(false)

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setErrorText('Нет токена авторизации.')
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await checkTelegramAuthToken(token)
        if (cancelled) return
        if (res.status === 'confirmed') {
          setStatus('success')
          return
        }
        setStatus('preview')
      } catch (err) {
        if (cancelled) return
        const s = getStatus(err)
        if (s === 404) setErrorText('Токен недействителен. Запросите новый в браузере.')
        else setErrorText('Не удалось проверить токен авторизации.')
        setStatus('error')
      }
    })()
    return () => { cancelled = true }
  }, [token])

  const runConfirm = async () => {
    setStatus('confirming')
    setErrorText('')
    try {
      await confirmTelegramAuthToken(token)
      await invalidateProfile()
      setStatus('success')
    } catch (err) {
      const s = getStatus(err)
      const detail = getErrorDetail(err) ?? ''
      if (s === 400 && /email/i.test(detail)) {
        // Требуется привязать email — открываем LinkEmailModal, по закрытию повторяем confirm.
        setPendingEmailRetry(true)
        setLinkEmailOpen(true)
        setStatus('preview')
        return
      }
      if (s === 409) setErrorText('Токен используется другим Telegram-аккаунтом.')
      else if (s === 404) setErrorText('Токен недействителен или истёк.')
      else if (s === 403) setErrorText('Откройте это окно в Telegram.')
      else setErrorText('Не удалось подтвердить авторизацию.')
      setStatus('error')
    }
  }

  const handleConfirm = () => { void runConfirm() }

  const handleEmailModalClose = async () => {
    setLinkEmailOpen(false)
    if (!pendingEmailRetry) return
    setPendingEmailRetry(false)
    // После привязки email автоматически пробуем confirm снова.
    try {
      const profile = await getProfile()
      setProfile(profile)
      if (profile.email) void runConfirm()
    } catch { /* игнор — покажем preview, юзер может нажать ещё раз */ }
  }

  const goHome = () => navigate(ROUTES.HOME, { replace: true })
  const closeApp = () => platform.close()

  if (status === 'checking' || status === 'confirming') {
    return <div className={styles.pending}>
      {status === 'checking' ? 'Проверяем токен…' : 'Подтверждаем авторизацию…'}
    </div>
  }

  if (status === 'error') {
    return (
      <div className={styles.screen}>
        <div className={styles.header}>
          <AlertCircle className={styles.icon} strokeWidth={1.5} aria-hidden="true" />
          <div className={styles.headings}>
            <h1 className={styles.title}>Авторизация в веб</h1>
            <p className={styles.error}>{errorText}</p>
          </div>
        </div>
        <div className={styles.actions}>
          <button type="button" onClick={goHome} className={styles.primary}>В приложение</button>
        </div>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div className={styles.screen}>
        <div className={styles.header}>
          <CheckCircle2 className={styles.icon} strokeWidth={1.5} aria-hidden="true" />
          <div className={styles.headings}>
            <h1 className={styles.title}>Авторизация подтверждена</h1>
            <p className={styles.text}>Возвращайтесь в браузер — вы уже вошли в веб-версию.</p>
          </div>
        </div>
        <div className={styles.actions}>
          <button type="button" onClick={closeApp} className={styles.primary}>Закрыть</button>
          <button type="button" onClick={goHome} className={styles.secondary}>В приложение</button>
        </div>
      </div>
    )
  }

  // preview
  return (
    <>
      <div className={styles.screen}>
        <div className={styles.header}>
          <AlertCircle className={styles.icon} strokeWidth={1.5} aria-hidden="true" />
          <div className={styles.headings}>
            <h1 className={styles.title}>Войти в веб-версию</h1>
            <p className={styles.text}>
              Подтвердите вход в веб-версию Pauline Cake с этого Telegram-аккаунта.
            </p>
          </div>
        </div>
        <div className={styles.actions}>
          <button type="button" onClick={handleConfirm} className={styles.primary}>Подтвердить</button>
          <button type="button" onClick={goHome} className={styles.secondary}>Отмена</button>
        </div>
      </div>

      {linkEmailOpen && <LinkEmailModal onClose={handleEmailModalClose} />}
    </>
  )
}
